import { useEffect, useMemo, useState } from "react";
import HeroSearch from "../site/HeroSearch.jsx";
import { CheckBadgeIcon, PriceTagIcon, GiftIcon, ShieldIcon, LocalIcon, FlexIcon, StarIcon, PinIcon, ArrowRightIcon, ChevronRightIcon } from "../site/icons.jsx";
import { ARTICLES, articleHref, mergeArticles } from "../data/articles.js";
import { lodgingNode, listOfferNode, itemListNode, guideNode, useJsonLd, useHead, hawaiiPlace, absUrl, CONTEXT, WEBSITE_ID, ORG_ID, SITE, ISLAND_NAMES } from "../lib/seo.js";

const IMAGE_ROOT = "https://images.aloharates.com/properties";
const FEATURED_IDS = ["lp323d4", "lp223fb", "lp1ddad", "lp1b90f"];

const ISLANDS = [
  { name: "Oʻahu",           slug: "oahu",       tagline: "Waikīkī, North Shore & Honolulu",   image: "https://home.aloharates.com/home_page/oahu.webp" },
  { name: "Maui",            slug: "maui",       tagline: "Beaches, resorts & the Road to Hāna", image: "https://home.aloharates.com/home_page/maui.webp" },
  { name: "Kauaʻi",          slug: "kauai",      tagline: "Cliffs, rainforests & quiet beaches", image: "https://home.aloharates.com/home_page/kauai.webp" },
  { name: "Hawaiʻi Island",  slug: "big-island", tagline: "Volcanoes, Kona & epic adventures",   image: "https://home.aloharates.com/home_page/big_island.webp" },
  { name: "Molokaʻi",        slug: "molokai",    tagline: "Empty beaches, sea cliffs, old Hawaiʻi", image: "https://home.aloharates.com/home_page/molokai.webp" },
  { name: "Lānaʻi",          slug: "lanai",      tagline: "Hulopoʻe Bay & luxury escapes",       image: "https://home.aloharates.com/home_page/lanai.webp" }
];

const pad = n => String(n).padStart(2, "0");
const iso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

function ratePrice(hotel, nights = 3) {
  const options = [];
  for (const room of hotel?.roomTypes || []) for (const rate of room.rates || []) {
    const total = Number(rate?.retailRate?.total?.[0]?.amount) || 0;
    const commission = Array.isArray(rate?.commission) ? Number(rate.commission[0]?.amount) || 0 : Number(rate?.commission?.amount) || 0;
    let inc = 0, exc = 0;
    for (const fee of rate?.retailRate?.taxesAndFees || []) { if (fee?.included) inc += Number(fee.amount) || 0; else exc += Number(fee.amount) || 0; }
    if (inc > 0.8 * total) inc = 0;
    if (total > 0) options.push({ nightly: Math.max(0, Math.round((total - commission - inc) / nights)), total: Math.max(0, Math.round(total + exc)) });
  }
  return options.sort((a, b) => a.nightly - b.nightly)[0] || null;
}

export default function HomePage() {
  const [properties, setProperties] = useState([]);
  const [prices, setPrices] = useState({});
  const [articles, setArticles] = useState(ARTICLES);
  const stay = useMemo(() => {
    const ci = addDays(new Date(), 14);
    const co = addDays(ci, 3);
    return { checkin: iso(ci), checkout: iso(co), nights: 3 };
  }, []);

  useEffect(() => {
    let live = true;
    fetch("/data/properties.json").then(r => r.json()).then(all => {
      if (!live) return;
      const byId = new Map(all.map(p => [p.id, p]));
      setProperties(FEATURED_IDS.map(id => byId.get(id)).filter(Boolean));
    }).catch(() => {});
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let live = true;
    fetch("/api/edit/posts")
      .then(r => r.ok ? r.json() : [])
      .then(remote => { if (live) setArticles(mergeArticles(remote)); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!properties.length) return;
    const controller = new AbortController();
    fetch("/api/rates", {
      method: "POST", headers: {"Content-Type":"application/json"},
      body: JSON.stringify({ hotelIds: properties.map(p => p.id), checkin: stay.checkin, checkout: stay.checkout, occupancies:[{adults:2}], currency:"USD", guestNationality:"US", maxRatesPerHotel:1, includeHotelData:true }),
      signal: controller.signal
    }).then(r => r.ok ? r.json() : null).then(payload => {
      if (!payload) return;
      const next = {};
      for (const hotel of payload.data || []) next[hotel.hotelId] = ratePrice(hotel, stay.nights);
      setPrices(next);
    }).catch(() => {});
    return () => controller.abort();
  }, [properties, stay]);

  // JSON-LD: the homepage of an online travel agency — WebPage with the
  // featured stays (typed Hotel / VacationRental, with the displayed total as
  // an offer for the shown dates), the six island collections, and the guides.
  const homeJsonLd = {
    "@context": CONTEXT,
    "@type": "WebPage",
    "@id": `${SITE.origin}/#webpage`,
    url: `${SITE.origin}/`,
    name: "Aloha Rates — Hawaiʻi hotels & vacation rentals at real local rates",
    description: SITE.description,
    isPartOf: { "@id": WEBSITE_ID },
    about: hawaiiPlace(),
    provider: { "@id": ORG_ID },
    mainEntity: itemListNode(properties.map(p => {
      const node = lodgingNode(p, { full: false });
      const offer = prices[p.id]?.total ? listOfferNode({ total: prices[p.id].total, checkin: stay.checkin, checkout: stay.checkout }) : undefined;
      if (offer) node.makesOffer = offer;
      return node;
    }), { name: "Today's Hawaiʻi stays we love" }),
    hasPart: [
      itemListNode(ISLANDS.map(i => ({ "@type": "CollectionPage", name: `Hotels & vacation rentals on ${ISLAND_NAMES[i.slug] || i.name}`, url: absUrl(`/${i.slug}/`) })), { name: "Islands" }),
      itemListNode((articles && articles.length ? articles : ARTICLES).slice(0, 4).map(a => ({ ...guideNode(a), "@context": undefined })), { name: "Guides from people who actually live here" })
    ]
  };
  useJsonLd("home-ldjson", homeJsonLd);
  useHead({
    title: "Aloha Rates — Hawaiʻi stays, guides, and better trips",
    description: SITE.description,
    canonical: "/"
  });

  return (
    <div className="bg-sand-50">
      <Hero/>
      <TrustStrip/>
      <IslandStrip/>
      <FeaturedDeals properties={properties} prices={prices}/>
      <HawaiiEdit articles={articles}/>
      <PerkAndStory/>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative">
      <div className="absolute inset-0 overflow-hidden">
        <img src="https://home.aloharates.com/home_page/hero.webp" alt="Hawaiian coastline at golden hour" className="h-full w-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-b from-ocean-900/70 via-ocean-900/55 to-ocean-900/80"/>
        <div className="absolute inset-x-0 top-0 h-[55%] bg-[radial-gradient(ellipse_at_center_top,rgba(8,20,30,0.55),transparent_70%)]"/>
      </div>
      <div className="relative container-page pt-28 md:pt-44 pb-16 md:pb-28 text-center text-white">
        <h1 className="font-display font-semibold text-5xl md:text-7xl leading-[1.05] tracking-tight text-white [text-shadow:0_2px_30px_rgba(0,0,0,0.6),0_1px_2px_rgba(0,0,0,0.4)]">
          Hawaiʻi <span className="italic font-medium text-sunset-300 [text-shadow:0_2px_24px_rgba(255,130,60,0.35)]">starts</span> here.
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-base md:text-lg leading-relaxed text-white [text-shadow:0_2px_18px_rgba(0,0,0,0.55)]">
          Real Hawaiʻi rates, local guides, and direct relationships with the properties we know best — all in one place, all six islands.
        </p>
        <div className="relative z-40 mx-auto mt-10 max-w-4xl text-left">
          <HeroSearch variant="hero"/>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  const items = [
    { icon: CheckBadgeIcon, title: "Hawaiʻi only",     copy: "We're the Hawaiʻi-focused booking site. That's all we do." },
    { icon: PriceTagIcon,   title: "No hidden fees",   copy: "The price you see is the price you pay. Taxes included." },
    { icon: ShieldIcon,     title: "Secure booking",   copy: "Your data is protected. Payments processed securely." },
    { icon: LocalIcon,      title: "Local support",    copy: "Real people who know Hawaiʻi — not a call center." },
    { icon: FlexIcon,       title: "Flexible options", copy: "Most stays include free cancellation for peace of mind." }
  ];
  return (
    <section className="relative -mt-8 z-10">
      <div className="container-page">
        <div className="rounded-3xl bg-white shadow-cardHover ring-1 ring-ink/5 px-5 py-4 md:px-8 md:py-5">
          {/* Phones show the lead promise only, full width. Five items in two
              columns orphaned the last one and squeezed every line of copy;
              a scrolling row hid four of them behind a swipe nobody makes.
              md+ still lays all five across. */}
          <div className="md:grid md:grid-cols-5 md:gap-x-6">
            {items.map(({icon:Icon, title, copy}, i) => (
              <div key={title} className={`items-start gap-3 ${i === 0 ? "flex" : "hidden md:flex"}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ocean-50 text-ocean-600">
                  <Icon size={18}/>
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-ink leading-tight">{title}</div>
                  <div className="text-xs md:text-[11px] text-ink-muted leading-snug mt-1 md:mt-0.5">{copy}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function IslandStrip() {
  return (
    <section className="container-page py-20 md:py-24">
      <div className="flex items-end justify-between gap-6">
        <div>
          <span className="eyebrow">Six islands, one guide</span>
          <h2 className="section-title mt-2">Which island is calling?</h2>
        </div>
        <a href="/results/" className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-ocean-700 hover:text-ocean-800">
          See every stay
          <ArrowRightIcon size={16}/>
        </a>
      </div>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {ISLANDS.map(island => (
          <a key={island.slug} href={`/${island.slug}/`} className="group relative overflow-hidden rounded-3xl shadow-card hover:shadow-cardHover transition aspect-[4/5] block">
            <img src={island.image} alt={island.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"/>
            <div className="absolute inset-0 bg-gradient-to-t from-ocean-900/90 via-ocean-900/40 to-ocean-900/10"/>
            <div className="absolute inset-x-0 bottom-0 p-6 text-white">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/70">Explore</span>
              <h3 className="font-display text-3xl md:text-[2rem] leading-tight mt-1 text-white [text-shadow:0_1px_10px_rgba(0,0,0,0.35)]">{island.name}</h3>
              <p className="mt-2 max-w-[26ch] text-sm text-white/90 [text-shadow:0_1px_8px_rgba(0,0,0,0.4)]">{island.tagline}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sunset-300 group-hover:text-sunset-200">
                Discover {island.name}
                <ChevronRightIcon size={14}/>
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function FeaturedDeals({ properties, prices }) {
  const list = properties.length ? properties : Array.from({length:4}).map((_,i)=>({id:`sk-${i}`,skeleton:true}));
  return (
    <section className="border-y border-ink/5 bg-white">
      <div className="container-page py-20 md:py-24">
        <div className="flex items-end justify-between gap-6">
          <div>
            <span className="eyebrow">Editor's picks</span>
            <h2 className="section-title mt-2">Today's Hawaiʻi stays we love</h2>
            <p className="prose-body mt-2 max-w-xl">Hand-picked resorts and condos with live rates from real bookings. No fine print.</p>
          </div>
          <a href="/results/" className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-ocean-700 hover:text-ocean-800">
            See all live rates
            <ArrowRightIcon size={16}/>
          </a>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {list.map(p => <FeaturedCard key={p.id} property={p} price={prices[p.id]}/>)}
        </div>
      </div>
    </section>
  );
}

function FeaturedCard({ property, price }) {
  if (property.skeleton) return <div className="card overflow-hidden"><div className="aspect-[4/3] bg-sand-200 animate-pulse"/><div className="p-5 space-y-2"><div className="h-3 w-1/3 bg-sand-200 animate-pulse rounded"/><div className="h-4 w-3/4 bg-sand-200 animate-pulse rounded"/><div className="h-3 w-1/2 bg-sand-200 animate-pulse rounded"/></div></div>;
  const slug = (property.name||"").toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");
  const href = `/properties/${property.id}-${slug}`;
  return (
    <a href={href} className="card overflow-hidden group flex flex-col">
      <div className="relative aspect-[4/3] overflow-hidden bg-sand-100">
        <img src={`${IMAGE_ROOT}/${property.id}/0.webp`} alt={property.name} loading="lazy" className="h-full w-full object-cover transition duration-700 group-hover:scale-105" onError={e => { e.currentTarget.style.display = "none"; }}/>
        {property.rating >= 9 && <span className="absolute top-3 left-3 rounded-pill bg-sunset-500 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm">Top rated</span>}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-5">
        <div className="flex items-center gap-1.5 text-xs text-ink-muted">
          <PinIcon size={12}/>
          {[property.area || property.city, property.island].filter(Boolean).join(", ")}
        </div>
        <h3 className="font-display text-lg leading-snug text-ink line-clamp-2 min-h-[3.4em]">{property.name}</h3>
        {property.rating > 0 && (
          <div className="flex items-center gap-1 text-sm">
            <StarIcon size={14} className="text-sunset-500 fill-current"/>
            <span className="font-medium">{Number(property.rating).toFixed(1)}</span>
            <span className="text-ink-muted">· {(property.reviewCount||0).toLocaleString()} reviews</span>
          </div>
        )}
        <div className="mt-auto pt-3 border-t border-ink/5">
          {price ? (
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-2xl font-semibold text-ink">${price.nightly.toLocaleString()}<span className="text-xs font-normal text-ink-muted"> / night</span></div>
                <div className="text-[11px] text-ink-muted">${price.total.toLocaleString()} total · taxes included</div>
              </div>
              <span className="text-xs font-medium text-ocean-700">View →</span>
            </div>
          ) : (
            <div className="text-sm text-ink-muted">Check availability →</div>
          )}
        </div>
      </div>
    </a>
  );
}

function HawaiiEdit({ articles }) {
  const list = (articles && articles.length ? articles : ARTICLES).slice(0, 4);
  return (
    <section className="container-page py-20 md:py-24">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_2.5fr] gap-10 md:gap-14 items-start">
        <div className="md:sticky md:top-28">
          <span className="eyebrow">The Hawaiʻi Edit</span>
          <h2 className="section-title mt-2">Guides from people who actually live here.</h2>
          <p className="prose-body mt-4">Where to eat when you land. Which beach is worth the drive. What locals wish visitors knew. Read before you book — or after.</p>
          <a href="/edit/" className="mt-6 btn-secondary inline-flex">Read The Edit</a>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {list.map(article => (
            <a key={article.slug} href={articleHref(article)} className="card overflow-hidden group flex flex-col">
              <div className="aspect-[16/10] overflow-hidden bg-sand-100">
                <img src={article.img} alt={article.title} loading="lazy" className="h-full w-full object-cover transition duration-700 group-hover:scale-105"/>
              </div>
              <div className="p-6 flex flex-col gap-2">
                <span className="eyebrow">{article.tag}</span>
                <h3 className="font-display text-xl leading-snug">{article.title}</h3>
                <p className="text-sm text-ink-muted leading-relaxed line-clamp-3">{article.excerpt}</p>
                <span className="mt-3 text-sm font-medium text-ocean-700 group-hover:text-ocean-800">Read the guide →</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function PerkAndStory() {
  return (
    <section className="container-page pb-24">
      <div className="rounded-3xl overflow-hidden bg-white shadow-cardHover ring-1 ring-ink/5">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="relative min-h-[360px] md:min-h-full">
            <img src="https://home.aloharates.com/home_page/kauai.webp" alt="" className="absolute inset-0 h-full w-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-tr from-ocean-900/40 via-transparent to-transparent"/>
          </div>
          <div className="p-10 md:p-14 flex flex-col justify-center gap-10">
            <div>
              <span className="eyebrow">We only do Hawaiʻi</span>
              <h2 className="section-title mt-2">It's not just where we work. It's who we are.</h2>
              <p className="prose-body mt-4">We built Aloha Rates because we love sharing the Hawaiʻi we know and care about. From great rates to our favorite local spots — we're here to help you have a better trip, not just a booked one.</p>
            </div>
            <div className="rounded-2xl bg-sunset-50 p-6 md:p-7 ring-1 ring-sunset-200/60">
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sunset-500 text-white shadow-pill">
                  <GiftIcon size={20}/>
                </span>
                <div>
                  <div className="font-display text-xl text-ink">Perks on our direct-partner stays.</div>
                  <p className="mt-2 text-sm text-ink-soft leading-relaxed">On select properties we work with directly, you get complimentary extras — free breakfast, a resort credit, or an early check-in — at no added cost.</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="/about/" className="btn-secondary">Our story</a>
              <a href="/results/" className="btn-primary">Find your stay</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
